// The connect window of a library connector (« Connecteurs du bureau » or Administration →
// Connecteurs → CONNECTER / RECONNECTER / REPRENDRE): the state machine of the Claude login window
// of Administration → Moteurs (web/ui/admin/accounts.js openLogin) on the person's own login job —
// POST /api/connectors/library/:id/login starts it (or hands back the one running), GET polls it
// every 2 s, DELETE cancels it, POST …/login/callback {url} sends the address pasted from the page
// that does not load. Whoever signs in at the service gives that account to the connector (every
// agent granted it then uses it). REPRENDRE (`resume`) only reads the job (GET): a login that
// ended meanwhile is shown as it ended, never started again behind the person's back (Claude Code
// drops the stored login of a connector each time one starts); RECOMMENCER then starts a new one.
// Three steps: open the service's login page (a new tab; its host is always shown), sign in and
// allow, then paste the address of the page that does not load (http://localhost…) and VALIDER; a
// countdown to the job's expiry; RECOMMENCER after a failure.
// Not dismissible: a stray click beside it or Esc (after the trip to the service) would cancel the
// login; only the footer button closes it. Closing cancels a login still waiting for the address.
// The pasted address leaves its field as soon as it is sent and is never stored, logged or shown.
// A Shopify store (the Shopify CLI's login, server/connectors/shopify-cli.js) gets the same window
// with Shopify's words: the store's page (a store on another Shopify account: switch accounts
// without logging out), then the page that does not load at http://127.0.0.1:13387/auth/callback…
// (the CLI's fixed port: one login at a time in the office).
import { h, button, input, field, setAttr, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { errorText } from '../admin/model.js';
import { isJobActive, isJobCancellable, formatCountdown } from '../admin/accounts-model.js';
import { alertBox, externalLink, setLabel, setBusy } from '../admin/common.js';
import {
  connectJobOf, localConnectJob, jobText, safeAuthUrl, validateCallbackUrl, callbackPrefix, CALLBACK_MAX, LIBRARY_BASE,
} from './model.js';
import './strings.js';

/** Job polling period while the window is open. */
export const POLL_MS = 2000;
/**
 * The server replays the pasted address to the CLI's local page (10 s at most), then waits for the
 * token exchange: the request outlasts both.
 */
export const CALLBACK_TIMEOUT_MS = 30_000;
/** Countdown refresh. */
const TICK_MS = 1000;
/** Consecutive failed polls before the window gives up. */
const MAX_POLL_ERRORS = 5;
const FAILED_STATES = ['error', 'expired', 'cancelled'];
const LIVE_TONES = ['info', 'error', 'success', 'warn'];
/** The page that does not load after Shopify's, when the login page does not say (the CLI's fixed port). */
const SHOPIFY_CALLBACK_PREFIX = 'http://127.0.0.1:13387/';

/**
 * Opens the connect window of `connector` and follows its login job until it ends.
 * @param {object} ctx app context
 * @param {{id: string, label: string}} connector
 * @param {{onDone?: (job: object) => void, onClose?: (job: object) => void, resume?: boolean, pollMs?: number,
 *   now?: () => number, origin?: string}} [opts] onDone: the login succeeded (the drawer reads the
 *   list again); onClose: the window closed, with the last job seen; resume: follow the login
 *   already started (GET only, never a new one); pollMs, now (server clock, ms) and origin (the
 *   office's, whose own pages may be linked: the mock engine's simulated login) are for tests
 * @returns {import('../overlay.js').Layer}
 */
export function openConnectWindow(ctx, connector, {
  onDone, onClose, resume = false, pollMs = POLL_MS, now, origin = globalThis.location?.origin || '',
} = {}) {
  const name = connector.label || connector.id;
  // A Shopify store logs in through the Shopify CLI: Shopify's words in the steps.
  const shopify = connector.catalogId === 'shopify';
  const base = `${LIBRARY_BASE}/${encodeURIComponent(connector.id)}/login`;
  const serverNow = now || (() => Date.now() + (Number(ctx.store.get().serverOffset) || 0));
  let job = localConnectJob('starting');
  let seq = 0; // bumped by every change of `job`: a poll answer older than the last change is dropped
  let pollTimer = null;
  let tickTimer = null;
  let pollErrors = 0;
  let pendingStart = false;
  let sending = false;
  let closed = false;

  // --- view --------------------------------------------------------------------------------------
  const spin = h('span', { class: 'spinner adm-acc-spin', 'aria-hidden': 'true' });
  const live = h('p', { class: 'adm-acc-live', role: 'status', 'aria-live': 'polite' });
  const link = externalLink('', shopify ? t('pc.connect.openShopify') : t('pc.connect.open', { name }), { primary: true });
  const hostHint = h('p', { class: 'adm-acc-step-hint pc-host' });
  const badUrl = alertBox('danger', t('pc.connect.badUrl'));
  // Kept away from password managers and autofill: it is a one-time address, not a credential to save.
  const cbInput = input({
    name: 'pc-callback', autocomplete: 'off', autocapitalize: 'none', spellcheck: false, inputmode: 'url',
    maxLength: CALLBACK_MAX, placeholder: t(shopify ? 'pc.connect.placeholderShopify' : 'pc.connect.placeholder'),
    'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '',
  });
  const submitBtn = button(t('pc.connect.submit'), { variant: 'primary', type: 'submit' });
  const inline = h('div', { class: 'adm-acc-inline' }, cbInput, submitBtn);
  inline.input = cbInput;
  const cbField = field(t('pc.connect.field'), inline);
  const countdown = h('p', { class: 'adm-acc-countdown', hidden: true });
  const step = (title, ...rest) => h('li', { class: 'adm-acc-step' }, h('span', { class: 'adm-acc-step-title' }, title), ...rest);
  // Shopify: the start of the page that does not load, as the login page says it.
  const step3Hint = h('p', { class: 'adm-acc-step-hint' },
    shopify ? t('pc.connect.step3HintShopify', { prefix: SHOPIFY_CALLBACK_PREFIX }) : t('pc.connect.step3Hint'));
  const steps = h('ol', { class: 'adm-acc-steps pc-steps' },
    step(shopify ? t('pc.connect.step1Shopify') : t('pc.connect.step1', { name }), hostHint, link, badUrl),
    step(t(shopify ? 'pc.connect.step2Shopify' : 'pc.connect.step2'), h('p', { class: 'adm-acc-step-hint' }, t(shopify ? 'pc.connect.step2HintShopify' : 'pc.connect.step2Hint')),
      // Another Shopify account: switched without logging out (that would cut the stores already connected from this browser).
      shopify ? h('p', { class: 'adm-acc-step-hint muted pc-accounts' }, t('pc.connect.step2AccountsShopify')) : null),
    step(t('pc.connect.step3'),
      step3Hint,
      h('p', { class: 'adm-acc-step-hint muted' }, t('pc.connect.step3Why')),
      h('form', { class: 'adm-acc-form', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); sendCallback(); } }, cbField),
      countdown));
  const done = h('div', { class: 'adm-acc-done' }, h('p', { class: 'adm-acc-step-hint' }, t('pc.connect.doneHint')));
  const retryBtn = button(t('pc.connect.retry'), { variant: 'primary', icon: 'refresh', onClick: () => start() });
  const failed = h('div', { class: 'adm-acc-failed' }, retryBtn);
  let layer = null;
  const closeBtn = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.connect.title', { name }), subtitle: t(shopify ? 'pc.connect.subtitleShopify' : 'pc.connect.subtitle'), size: 'md',
    content: h('div', { class: 'stack adm-acc-login pc-connect' }, h('div', { class: 'adm-acc-statusrow' }, spin, live), steps, done, failed),
    footer: [closeBtn], className: 'adm-modal adm-acc-modal pc-modal', dismissible: false,
  });

  layer.onClose(() => {
    closed = true;
    stopTimers();
    cbInput.value = '';
    // Closing abandons a login still waiting for the address (the server stops the CLI); a job
    // being checked is left to finish (the drawer learns the result from the live events). A start
    // still on its way is cancelled when it answers.
    if (!pendingStart && isJobCancellable(job)) cancelJob();
    try { onClose?.(job); } catch (err) { console.error(err); }
  });

  function cancelJob() {
    Promise.resolve(ctx.api.del(base)).catch(() => {});
  }

  function stopTimers() {
    clearTimeout(pollTimer);
    pollTimer = null;
    stopTick();
  }

  function stopTick() {
    clearInterval(tickTimer);
    tickTimer = null;
  }

  /** Updates the countdown to the job's expiry; false once over (or when there is none). */
  function tick() {
    const end = job.state === 'waiting_code' ? job.expiresAt : null;
    if (!end) {
      countdown.hidden = true;
      return false;
    }
    const left = end - serverNow();
    countdown.hidden = false;
    setText(countdown, left > 0 ? t('pc.connect.countdown', { time: formatCountdown(left) }) : t('pc.connect.linkExpired'));
    return left > 0;
  }

  /** Shows `job` (status line, spinner, steps, footer button). */
  function show(prev) {
    const msg = jobText(job, name);
    setText(live, msg.text);
    for (const tone of LIVE_TONES) live.classList.toggle(`is-${tone}`, msg.tone === tone);
    spin.hidden = !(job.state === 'starting' || job.state === 'checking');
    setLabel(closeBtn, isJobCancellable(job) ? t('common.cancel') : t('common.close'));
    const waiting = job.state === 'waiting_code';
    const checking = job.state === 'checking';
    steps.hidden = !(waiting || checking);
    const safe = safeAuthUrl(job.url, { origin });
    if (safe) setAttr(link, 'href', safe.url); else link.removeAttribute('href');
    link.hidden = !safe;
    badUrl.hidden = !!safe;
    setText(hostHint, safe ? t('pc.connect.step1Hint', { host: safe.host }) : '');
    hostHint.hidden = !safe;
    if (shopify && safe) setText(step3Hint, t('pc.connect.step3HintShopify', { prefix: callbackPrefix(safe.url) || SHOPIFY_CALLBACK_PREFIX }));
    cbInput.disabled = checking;
    setBusy(submitBtn, checking);
    done.hidden = job.state !== 'done';
    failed.hidden = !FAILED_STATES.includes(job.state);
    if (waiting && tick()) {
      if (!tickTimer) tickTimer = setInterval(() => { if (!tick()) stopTick(); }, TICK_MS);
    } else {
      stopTick();
    }
    // The link takes the focus when the steps appear (on a phone, focusing the field would open the
    // keyboard over it); the field takes it back after a refused address (sendCallback).
    if (waiting && prev?.state !== 'waiting_code' && prev?.state !== 'checking') (safe ? link : closeBtn).focus();
    if (!done.hidden && prev?.state !== 'done') closeBtn.focus();
    if (!failed.hidden && !FAILED_STATES.includes(prev?.state)) retryBtn.focus();
  }

  /** Takes a new state of the job and polls while it runs. */
  function apply(next) {
    if (closed) return;
    seq += 1;
    const prev = job;
    job = next || localConnectJob('error', t('pc.job.lost'));
    show(prev);
    if (isJobActive(job)) {
      schedulePoll();
      return;
    }
    stopTimers();
    if (job.state === 'done' && prev.state !== 'done') {
      try { onDone?.(job); } catch (err) { console.error(err); }
    }
  }

  function schedulePoll() {
    clearTimeout(pollTimer);
    pollTimer = null;
    if (closed || sending || pendingStart || !isJobActive(job)) return;
    pollTimer = setTimeout(poll, pollMs);
  }

  async function poll() {
    pollTimer = null;
    if (closed || sending) return;
    const mine = seq;
    try {
      const res = await ctx.api.get(base);
      if (closed || sending || mine !== seq) return;
      pollErrors = 0;
      apply(connectJobOf(res));
    } catch (err) {
      if (closed || sending || mine !== seq) return;
      pollErrors += 1;
      if ([401, 403, 404].includes(err?.status) || pollErrors >= MAX_POLL_ERRORS) apply(localConnectJob('error', errorText(err)));
      else schedulePoll();
    }
  }

  async function start() {
    stopTimers();
    seq += 1;
    const prev = job;
    job = localConnectJob('starting');
    show(prev);
    cbField.setError(null);
    pendingStart = true;
    pollErrors = 0;
    const mine = seq;
    let next = null;
    let failure = null;
    try {
      next = connectJobOf(await ctx.api.post(base, {}));
    } catch (err) {
      failure = err;
    }
    pendingStart = false;
    if (closed) {
      if (isJobCancellable(next)) cancelJob();
      return;
    }
    if (mine !== seq) return;
    apply(failure ? localConnectJob('error', errorText(failure)) : next || localConnectJob('error', t('pc.job.noJob')));
  }

  /** Sends the pasted address (the server answers once the CLI has read it). */
  async function sendCallback() {
    if (sending || closed || job.state !== 'waiting_code') return;
    const checked = validateCallbackUrl(cbInput.value, { authUrl: job.url });
    if (!checked.ok) {
      cbField.setError(checked.error);
      cbInput.focus();
      return;
    }
    cbField.setError(null);
    cbInput.value = '';
    sending = true;
    clearTimeout(pollTimer);
    pollTimer = null;
    const waiting = job;
    seq += 1;
    job = { ...waiting, state: 'checking', message: '' };
    show(waiting);
    let res = null;
    let failure = null;
    try {
      res = await ctx.api.post(`${base}/callback`, { url: checked.value }, { timeoutMs: CALLBACK_TIMEOUT_MS });
    } catch (err) {
      failure = err;
    }
    sending = false;
    if (closed) return;
    if (!failure) {
      apply(connectJobOf(res));
    } else if (failure.status === 400) {
      apply(waiting);
      cbField.setError(errorText(failure));
      cbInput.focus();
    } else {
      // 409 (no login waits for an address any more), timeout or network: say so, then read the job.
      ctx.toast.error(errorText(failure));
      seq += 1;
      poll();
    }
  }

  /** REPRENDRE: the job as the server has it (still running, or how it ended), no new login. */
  async function follow() {
    seq += 1;
    const mine = seq;
    pendingStart = true;
    let next = null;
    let failure = null;
    try {
      next = connectJobOf(await ctx.api.get(base));
    } catch (err) {
      failure = err;
    }
    pendingStart = false;
    if (closed) {
      // Closed before the answer: like a close while it waits (the login is abandoned).
      if (isJobCancellable(next)) cancelJob();
      return;
    }
    if (mine !== seq) return;
    apply(failure ? localConnectJob('error', errorText(failure)) : next || localConnectJob('error', t('pc.job.lost')));
  }

  if (resume) follow(); else start();
  return layer;
}
